<script setup lang="ts">
import { PlusCircle, X } from 'lucide-vue-next'
import ListSelector from './ListSelector.vue'
import CreateListForm from './CreateListForm.vue'
import { Button } from '@/components/ui/button'
import {
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import type { Elemental, ElementalTypeEnum } from '~~/src/types/elemental'
import { useGetElementalParentsQuery } from '~~/src/composables/query/elementals/useGetElementalParentsQuery'
import type { GetElementalParentsQuery } from '~~/src/api/elementals'
import type { List } from '~~/src/types/list'
const props = defineProps<Props>()
const emits = defineEmits<{
  (event: 'toggleShowCreate', value: boolean): void
}>()
defineOptions({
  name: 'SaveElementalMenuItem',
})

interface Props {
  elemental?: Elemental | null
  type?: ElementalTypeEnum | null
  listId?: string
  showCreate?: boolean
}
const query = computed(() => ({ elementIds: [props.elemental?.id], limit: 1000 }) as GetElementalParentsQuery)

const { data, isLoading, refetch } = useGetElementalParentsQuery({ query })
const listIds = ref<string[]>([])

watch(data, () => {
  if (data.value)
    listIds.value = data.value?.[0] ? data.value[0].parents.map(parent => parent.id) : []
})

const handlerSaveOnList = (list: List) => {
  const listId = (list.id)
  const isOnList = listIds.value.includes(listId)
  if (!isOnList)
    return listIds.value = [...listIds.value, listId]
  listIds.value = listIds.value.filter(id => id !== listId)
}

const handlerSave = () => {
  emits('toggleShowCreate', false)
  refetch()
}

onMounted(() => {
  if (data.value)
    listIds.value = data.value?.[0] ? data.value[0].parents.map(parent => (parent.id)) : []
  refetch()
})
</script>

<template>
  <div class="contents">
    <DropdownMenuLabel class="flex items-center justify-between">
      <span class="text-darkGray font-700 font-inter text-xs">Save to Lists</span>
      <div class="ml-a flex items-center gap-1">
        <span v-if="!showCreate" class="text-xs text-softBlack font-700 font-inter">
          New List
        </span>
        <Button v-if="!showCreate" variant="snack-ghost" size="icon" class="w-5 h-5" @click="emits('toggleShowCreate', true)">
          <PlusCircle stroke="white" fill="softBlack" />
        </Button>
        <Button v-else variant="snack-ghost" size="icon" class="w-5 h-5" @click="emits('toggleShowCreate', false)">
          <X class="text-softBlack" />
        </Button>
      </div>
    </DropdownMenuLabel>
    <DropdownMenuSeparator class="mb-3" />
    <TransitionFade>
      <CreateListForm v-if="showCreate" :element="elemental" :type="type" class="px-4" @on-create="handlerSave()" />
      <ListSelector v-else :is-loading="isLoading" :element="elemental" :list-ids="listIds" :type="type" :save-on-select="true" class="px-4" @selected="handlerSaveOnList($event)" />
    </TransitionFade>
  </div>
</template>
